import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Grid, TextField, Theme, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createStyles, makeStyles } from '@mui/styles';
import { ChangeEvent, FC, useState } from 'react';

import CreditCards from '../credit_cards.svg';
import { WePayTokenResponse } from '../custom-types/WePayTokenResponse';
import { AcceptedPaymentMethod, PaymentRequestInfo, Person } from '../gql-types.generated';
import { AllAcceptedPaymentMethodForDisplay } from '../util/AcceptedPaymentMethodForDisplay';
import { checkIsFullNameValid } from '../util/Validators';
import { tokenizeCCInfo } from '../util/WePay';
import LoadingMask from './LoadingMask';
import WePayCreditCapture from './WePayCreditCapture';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    padding: {
      padding: theme.spacing(2),
    },
    iFramePadding: {
      padding: theme.spacing(0, 0, 1.5, 0),
    },
    iFramePaddingGuest: {
      padding: theme.spacing(0, 0, 1.5, 0),
      position: 'relative',
    },
    paymentAction: {
      marginTop: 6,
      marginLeft: 4,
    },
    creditCardInformationWrap: {
      padding: theme.spacing(1.5),
      flexGrow: 1,
    },
    rightAlign: {
      marginTop: -6,
      textAlign: 'right',
    },
    creditCardInfoText: {
      width: 180,
    },
    creditCardImage: {
      width: 'calc(100% - 180px)',
      minWidth: 180,
    },
    bannerPadding: {
      padding: theme.spacing(0, 1, 1, 2),
    },
    holderName: {
      padding: theme.spacing(0, 2, 1, 2),
    },
    holderNameFieldError: {
      '& ::placeholder': {
        color: theme.palette.error.main,
        opacity: 1,
      },
    },
  }),
);

interface CardInfoProps {
  backText?: string;
  creditError?: Error;
  handleBack?: () => void;
  onTokenizeInjectSuccess?: (iframe: { tokenize: () => Promise<WePayTokenResponse> }) => void;
  tokenizeSuccess?: (token: string) => void;
  viewerUser?: Person;
  paymentRequestInfo?: PaymentRequestInfo;
  holderName: string;
  setHolderName: (cardHolderName: string) => void;
  holderNameValid: boolean;
  setHolderNameValid: (holderNameValid: boolean) => void;
  isIFrameVisible: boolean;
  setIsIFrameVisible: (isIFrameVisible: boolean) => void;
  isGuestUser?: boolean;
  setSendingPayment?: (sendingPayment: boolean) => void;
  sendingPayment?: boolean;
  submitText?: string;
}

/*
  This component allows the input of credit card information within a WePay iFrame.
  It can be utilized in two ways:
    - tokenizeSuccess: Configured with a button return function to return the tokenized cc info.
        This puts the ownership of a button within this component.
    - onTokenizeInjectSuccess: Configure a function to accept the tokenize function which is used to use the WePay iFrame tokenize function.
        This allows the owner of this component the ability to submit the cc info for tokenize at their own pace via their own button.

  Error state is handled either internally or by the owning component.
*/
const CardInfo: FC<CardInfoProps> = props => {
  const classes = useStyles();
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const matchesMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const {
    backText,
    creditError,
    handleBack,
    onTokenizeInjectSuccess,
    tokenizeSuccess,
    viewerUser,
    paymentRequestInfo,
    holderName,
    setHolderName,
    holderNameValid,
    setHolderNameValid,
    isIFrameVisible,
    setIsIFrameVisible,
    isGuestUser,
    setSendingPayment,
    sendingPayment,
    submitText,
  } = props;
  const [creditCardIFrame, setCreditCardIFrame] = useState<{ tokenize: () => Promise<WePayTokenResponse> }>();
  const [error, setError] = useState<Error | undefined>();

  // allow making the cc payment if the user is logged with billing information or is doing a guest checkout, in which case the billing information is required to proceed
  const enablePayment = !!viewerUser?.payerProfile || !viewerUser;

  const onInjectIFrameSuccess = (iframe: { tokenize: () => Promise<WePayTokenResponse> }) => {
    // Capture the tokenize call for the credit card input.
    if (iframe) {
      setCreditCardIFrame(iframe);
      if (onTokenizeInjectSuccess) {
        onTokenizeInjectSuccess(iframe);
      }
    }
  };
  const onTokenizeSuccess = (token: string) => {
    if (token && tokenizeSuccess) {
      tokenizeSuccess(token);
    }
  };
  const onTokenizeFailure = (error: Error) => {
    if (error) {
      setError(error as Error);
      if (setSendingPayment) setSendingPayment(false);
    }
  };
  const onCreditSubmit = () => {
    setHolderNameValid(checkIsFullNameValid(holderName));
    if (creditCardIFrame?.tokenize && checkIsFullNameValid(holderName)) {
      if (setSendingPayment) setSendingPayment(true);
      tokenizeCCInfo(creditCardIFrame?.tokenize, onTokenizeSuccess, onTokenizeFailure);
    }
  };
  const onHolderNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value as string;
    setHolderName(name);
    setHolderNameValid(checkIsFullNameValid(name));
  };

  // Credit card iFrame docs
  // https://dev.wepay.com/docs/link-integration/process-payments#credit-card-iframes
  return (
    <Box>
      <Grid container justifyContent="space-between" className={classes.creditCardInformationWrap}>
        <Grid item className={classes.creditCardInfoText}>
          <Typography marginTop={1} id="credit-card-information">
            Credit card information
          </Typography>
        </Grid>
        <Grid item className={`${matchesMdDown && !matches ? '' : classes.rightAlign} ${classes.creditCardImage}`}>
          <img src={CreditCards} alt="credit_cards"></img>
        </Grid>
      </Grid>
      {paymentRequestInfo &&
        paymentRequestInfo?.acceptedPaymentMethods &&
        (paymentRequestInfo.acceptedPaymentMethods.length < 7 ||
          (paymentRequestInfo.acceptedPaymentMethods.length === 7 &&
            paymentRequestInfo.acceptedPaymentMethods.indexOf(AcceptedPaymentMethod.Unknown) !== -1)) && (
          <Grid className={classes.bannerPadding}>
            <Typography>{`Some payment methods are not supported in this region. Please use a supported payment method (${paymentRequestInfo.acceptedPaymentMethods
              .map(method => {
                return AllAcceptedPaymentMethodForDisplay.find(data => data.key === method)?.value;
              })
              .join(', ')}).`}</Typography>
          </Grid>
        )}
      {isIFrameVisible && (
        <Grid item xs={12} className={classes.holderName}>
          <TextField
            variant={'outlined'}
            fullWidth
            autoFocus
            error={!holderNameValid}
            InputLabelProps={{ shrink: false }}
            value={holderName}
            onChange={onHolderNameChange}
            placeholder="Card Holder Name"
            inputProps={{
              maxLength: 26,
              'aria-label': 'Input field for the name of the card holder',
              'aria-placeholder': 'Card Holder Name',
            }}
            className={holderNameValid ? undefined : classes.holderNameFieldError}
            data-cy="holder-name"
          />
        </Grid>
      )}
      <Box className={isGuestUser ? classes.iFramePaddingGuest : classes.iFramePadding}>
        {isGuestUser === true && <LoadingMask loading={!isIFrameVisible} />}
        <WePayCreditCapture injectSuccess={onInjectIFrameSuccess} iFrameId={'cc_iframe'} setIsIframeVisible={setIsIFrameVisible} />
      </Box>
      {(tokenizeSuccess || handleBack) && (
        <Grid container justifyContent="space-between" className={classes.padding}>
          {handleBack && (
            <Button
              className={classes.paymentAction}
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                handleBack();
              }}
              data-cy="back-to-billing"
            >
              {backText || 'Back'}
            </Button>
          )}
          {tokenizeSuccess && (
            <Button
              className={classes.paymentAction}
              variant="contained"
              color="primary"
              onClick={() => {
                onCreditSubmit();
              }}
              data-cy="make-payment"
              disabled={!enablePayment || sendingPayment}
            >
              {submitText || 'Make Payment'}
            </Button>
          )}
        </Grid>
      )}
      {(error?.message || creditError?.message) && (
        <Grid container justifyContent="space-between" className={classes.padding}>
          <Typography color="error" variant="subtitle2">
            {error?.message || creditError?.message}
          </Typography>
        </Grid>
      )}
    </Box>
  );
};

export default CardInfo;
